// GoogleLoginComponent.js
// Inline AppBar element — no fixed positioning. Designed to live inside a Toolbar.
import React, { useState } from 'react';
import { GoogleLogin, googleLogout } from '@react-oauth/google';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { useUser } from '../contexts/UserContext';
import { useGame } from '../contexts/GameContext';
import apiClient from '../services/api';

const IS_NATIVE = Capacitor.isNativePlatform();

const GoogleLoginComponent = () => {
  const { user, handleUserLogin, handleUserLogout, isLoadingProgress } = useUser();
  const gameContext = useGame();
  const [nativeError, setNativeError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // ── Shared: send idToken to backend ────────────────────────────────────────
  const verifyAndLogin = async (idToken) => {
    const response = await apiClient.post('/auth/google', { token: idToken });
    const data = response.data;
    if (data.user) {
      const alreadyKnown = user && user.sub === data.user?.sub;
      await handleUserLogin(data.user, gameContext, { skipSync: alreadyKnown });
    } else {
      throw new Error(data.error || 'Login failed');
    }
  };

  // ── Web ─────────────────────────────────────────────────────────────────────
  const handleWebLogin = async (credentialResponse) => {
    await verifyAndLogin(credentialResponse.credential);
  };

  // ── Native iOS ──────────────────────────────────────────────────────────────
  const handleNativeLogin = async () => {
    setNativeError(null);
    try {
      const googleUser = await GoogleAuth.signIn();
      const idToken = googleUser?.authentication?.idToken;
      if (!idToken) throw new Error('No idToken returned');
      await verifyAndLogin(idToken);
    } catch (err) {
      const msg = err?.message || err?.toString() || 'Unknown error';
      setNativeError(msg);
    }
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    if (IS_NATIVE) {
      try { await GoogleAuth.signOut(); } catch (_) {}
    } else {
      googleLogout();
    }
    await handleUserLogout(gameContext);
  };

  // ── Logged in: avatar → dropdown ───────────────────────────────────────────
  if (user) {
    return (
      <>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ p: 0.5, ml: 0.5 }}
          aria-label="Account menu"
        >
          {isLoadingProgress ? (
            <CircularProgress size={28} sx={{ color: 'rgba(255,255,255,0.8)' }} />
          ) : user.picture ? (
            <Avatar
              src={user.picture}
              alt={user.name}
              sx={{ width: 32, height: 32, border: '2px solid rgba(255,255,255,0.55)' }}
            />
          ) : (
            <AccountCircleIcon sx={{ color: 'white', fontSize: 32 }} />
          )}
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 4,
            sx: { mt: 0.5, minWidth: 200, borderRadius: 2 },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
              {user.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {user.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#f44336' }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#f44336' }} />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </>
    );
  }

  // ── Not logged in ───────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {IS_NATIVE ? (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={handleNativeLogin}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.6)',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 5,
              px: 2,
              py: 0.5,
              fontSize: '0.8rem',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.12)',
              },
            }}
          >
            Sign In
          </Button>
          {nativeError && (
            <Typography
              variant="caption"
              sx={{ color: '#ffcdd2', mt: 0.5, maxWidth: 140, textAlign: 'right', fontSize: '0.65rem' }}
            >
              {nativeError}
            </Typography>
          )}
        </>
      ) : (
        <Box sx={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
          <GoogleLogin
            onSuccess={handleWebLogin}
            onError={() => {}}
            useOneTap
            shape="pill"
            size="medium"
            text="signin"
            theme="outline"
          />
        </Box>
      )}
    </Box>
  );
};

export default GoogleLoginComponent;
